import React from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { fetchManualTimeJobs, type TimeJob, type TimeJobListKind, type TimePerson } from '@/data/scheduleTimeTracking';
import SearchSelect from '../components/form/SearchSelect';
import TimeEmployeePicker from '../components/time/TimeEmployeePicker';
import { useDebouncedSearch } from '../components/time/useDebouncedSearch';
import { safeErrorMessage } from '../utils/appErrors';

const jobKey = (job: TimeJob) => `${job.kind}:${job.id}`;

function useManualTimeJobs(locationId: string, authorizationKey: string, kind: TimeJobListKind, search: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['schedule-time-manual-jobs', authorizationKey, locationId, kind, search], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => fetchManualTimeJobs(locationId, kind, search, pageParam),
    getNextPageParam: page => page.has_more ? page.jobs[page.jobs.length - 1]?.id : undefined,
    placeholderData: keepPreviousData, enabled,
  });
}

/** Keep selections independent of search pages and background refreshes. */
export default function ManualTimeEntryFields({ locationId, authorizationKey, busy, person, job, onPerson, onJob, validation }: {
  locationId: string; authorizationKey: string; busy: boolean; person: TimePerson | null; job: TimeJob | null;
  onPerson: (value: TimePerson | null) => void; onJob: (value: TimeJob | null) => void; validation: Record<string, string>;
}) {
  const [jobSearch, setJobSearch] = React.useState('');
  // The job searches start when the Job field first opens, not when the form does.
  const [jobsOpened, setJobsOpened] = React.useState(false);
  const jobTerm = useDebouncedSearch(jobSearch);
  // One Job field searches every kind of job; each option names its kind.
  const installations = useManualTimeJobs(locationId, authorizationKey, 'installation', jobTerm, jobsOpened);
  const services = useManualTimeJobs(locationId, authorizationKey, 'service', jobTerm, jobsOpened);
  const assetServices = useManualTimeJobs(locationId, authorizationKey, 'asset_service', jobTerm, jobsOpened);
  const jobLists = [{ label: 'Installation', query: installations }, { label: 'External service', query: services }, { label: 'Asset service', query: assetServices }];
  // Rows keep load order (each kind's first page, then each kind's next page), so Load more adds at the bottom.
  const pageCount = Math.max(0, ...jobLists.map(list => list.query.data?.pages.length ?? 0));
  const jobChoices = Array.from({ length: pageCount }, (_, index) => index).flatMap(index => jobLists.flatMap(list =>
    (list.query.data?.pages[index]?.jobs ?? []).map(row => ({
      job: row, option: { id: jobKey(row), label: `${row.code} · ${row.name}`, detail: `${list.label} · ${row.status.replace(/_/g, ' ')}` },
    }))));
  const failedJobs = jobLists.filter(list => list.query.isError);
  const id = React.useId();
  return <div className="schedule-time__manual-fields">
    <div className="schedule-time__fields">
      <TimeEmployeePicker id={`${id}-person`} locationId={locationId} authorizationKey={authorizationKey} value={person} onChange={onPerson} disabled={busy}
        invalid={Boolean(validation.person)} describedBy={validation.person ? `${id}-person-error` : undefined}>
        {validation.person ? <span className="schedule-time__error" id={`${id}-person-error`}>{validation.person}</span> : null}
      </TimeEmployeePicker>
      <SearchSelect id={`${id}-job`} label="Job" value={job ? { id: jobKey(job), label: `${job.code} · ${job.name}` } : null}
        options={jobChoices.map(choice => choice.option)} search={jobSearch} onSearchChange={text => { setJobsOpened(true); setJobSearch(text); }}
        onChange={option => onJob(jobChoices.find(choice => choice.option.id === option.id)?.job ?? null)}
        placeholder="Type a job code or name" loading={jobLists.some(list => list.query.isPending || list.query.isPlaceholderData) || jobTerm !== jobSearch.trim()}
        error={failedJobs.length ? safeErrorMessage(failedJobs[0].query.error, 'Jobs could not be loaded.') : undefined}
        onRetry={() => { failedJobs.forEach(list => { void list.query.refetch(); }); }}
        emptyText="No matching jobs. Try another name or code." hasMore={jobLists.some(list => list.query.hasNextPage)}
        loadingMore={jobLists.some(list => list.query.isFetchingNextPage)} loadMoreLabel="Load more jobs"
        onLoadMore={() => { jobLists.forEach(list => { if (list.query.hasNextPage) void list.query.fetchNextPage(); }); }}
        required disabled={busy} invalid={Boolean(validation.job)} describedBy={validation.job ? `${id}-job-error` : undefined}>
        {validation.job ? <span className="schedule-time__error" id={`${id}-job-error`}>{validation.job}</span> : null}
      </SearchSelect>
    </div>
    <p className="fg-muted text-sm">Completed jobs are included. You can add time only for employees you are authorized to manage.</p>
  </div>;
}
